import React, { useEffect } from 'react';
import {
  Heading,
  PrimaryButton,
  SpinningLoader,
  Table,
  TableColumnConfig,
  DropdownField,
  useFormField,
  Text,
} from '@weave/design-system';
import { css } from '@emotion/core';
import { WeaveTheme } from '@weave/theme-original';
import moment from 'moment';
import { getStatusColor } from '../fax-test-section.utils';
import { FaxItem } from '../fax-test-section.types';

interface FaxListTableProps {
  title: string;
  faxes: FaxItem[];
  isLoading: boolean;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onRefresh: () => void;
  onFaxIdClick: (faxId: string) => void;
  onCopyToClipboard: (text: string) => void;
  direction: 'sent' | 'received';
}

const PAGE_SIZE_OPTIONS = [
  { value: '5', label: '5' },
  { value: '10', label: '10' },
  { value: '15', label: '15' },
  { value: '20', label: '20' },
  { value: '25', label: '25' },
];

const containerStyles = (theme: WeaveTheme) => css`
  margin-bottom: ${theme.spacing(4)};
  padding: ${theme.spacing(2)};
  background-color: ${theme.colors.gray['100']};
  border: 1px solid ${theme.colors.gray['300']};
  border-radius: ${theme.borderRadius.small};
`;

const headerStyles = (theme: WeaveTheme) => css`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  margin-bottom: ${theme.spacing(2)};
  gap: ${theme.spacing(2)};
`;

const statusBadgeStyles = (statusColor: string) => css`
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: bold;
  color: white;
  background-color: ${statusColor};
`;

const clickableCellStyles = css`
  cursor: pointer;
  &:hover {
    color: #007bff;
    text-decoration: underline;
  }
`;

export const FaxListTable: React.FC<FaxListTableProps> = ({
  title,
  faxes,
  isLoading,
  pageSize,
  onPageSizeChange,
  onRefresh,
  onFaxIdClick,
  onCopyToClipboard,
  direction,
}) => {
  const pageSizeField = useFormField({
    type: 'dropdown',
    value: String(pageSize),
  });

  // Handle page size changes
  useEffect(() => {
    const newSize = Number(pageSizeField.value);
    if (newSize !== pageSize && !isNaN(newSize)) {
      onPageSizeChange(newSize);
    }
  }, [pageSizeField.value, pageSize, onPageSizeChange]);

  const columnConfig: TableColumnConfig<FaxItem>[] = [
    {
      Header: 'From',
      accessor: (row) =>
        direction === 'sent'
          ? row.locationPhone || 'N/A'
          : row.contactPhone || 'N/A',
      id: 'from',
      cellRenderer: (value: string) => (
        <Text
          css={clickableCellStyles}
          title="Click to copy"
          onClick={() => onCopyToClipboard(value)}
        >
          {value}
        </Text>
      ),
    },
    {
      Header: 'To',
      accessor: (row) =>
        direction === 'sent'
          ? row.contactPhone || 'N/A'
          : row.locationPhone || 'N/A',
      id: 'to',
      cellRenderer: (value: string) => (
        <Text
          css={clickableCellStyles}
          title="Click to copy"
          onClick={() => onCopyToClipboard(value)}
        >
          {value}
        </Text>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      id: 'status',
      cellRenderer: (value: string) => {
        const statusVal = value || 'N/A';
        return (
          <Text
            css={css`cursor: pointer;`}
            title="Click to copy"
            onClick={() => onCopyToClipboard(statusVal)}
          >
            <span css={statusBadgeStyles(getStatusColor(statusVal))}>
              {statusVal}
            </span>
          </Text>
        );
      },
    },
    {
      Header: 'Created',
      accessor: 'createdAt',
      id: 'createdAt',
      cellRenderer: (value: string) => {
        const createdVal = value ? moment(value).format('MMM D, h:mm A') : 'N/A';
        return (
          <Text
            css={clickableCellStyles}
            title="Click to copy"
            onClick={() => onCopyToClipboard(createdVal)}
          >
            {createdVal}
          </Text>
        );
      },
    },
    {
      Header: 'Fax ID (click to use)',
      accessor: 'id',
      id: 'faxId',
      cellRenderer: (value: string) => {
        const faxIdVal = value || 'N/A';
        return (
          <Text
            css={css`
              cursor: pointer;
              max-width: 200px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              &:hover {
                color: #007bff;
                text-decoration: underline;
              }
            `}
            title="Click to use in Check Status"
            onClick={() => onFaxIdClick(faxIdVal)}
          >
            {faxIdVal}
          </Text>
        );
      },
    },
  ];

  return (
    <div css={containerStyles}>
      {/* Header: 3-column grid layout */}
      <div css={headerStyles}>
        <PrimaryButton size="tiny" onClick={onRefresh} disabled={isLoading}>
          {isLoading ? <SpinningLoader size="small" /> : 'Refresh'}
        </PrimaryButton>
        <Heading
          level={2}
          css={css`
            margin: 0 !important;
            text-align: center !important;
          `}
        >
          {title}
        </Heading>
        <DropdownField
          {...pageSizeField}
          label=""
          name="pageSize"
          css={css`
            min-width: 80px;
          `}
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <DropdownField.Option key={option.value} value={option.value}>
              {option.label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>

      {faxes.length === 0 && !isLoading ? (
        <Text
          css={(theme: WeaveTheme) => css`
            color: ${theme.colors.gray['500']};
            font-style: italic;
            text-align: center;
            margin: ${theme.spacing(1)} 0;
          `}
        >
          No {direction} faxes found
        </Text>
      ) : (
        <Table
          data={faxes}
          colConfig={columnConfig}
          isLoading={isLoading}
          tableInstanceId={`fax-list-table-${direction}`}
        />
      )}
    </div>
  );
};
